import React, { useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  TextField,
  ClickAwayListener,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

import Section from "../../../layout/Section";
import SubSection from "../../../layout/SubSection";
import DialogTemplate from "../../../layout/DialogTemplate";
import Button from "../../../common/inputs/Button";
import EditableLabel from "../../../common/inputs/EditableLabel";
import { colors } from "../../../../styles/colors";
import { useAdminCapexTemplate } from "../hooks/useAdminCapexTemplate";
import { useToast } from "../../../../contexts/ToastContext";
import {
  AdminTemplateSection,
  AdminTemplateItem,
} from "../../../../services/api/admin";

interface InvestmentskostenSectionProps {
  orgId: string;
}

/**
 * Einzelne Positions-Zeile
 */
interface TemplateItemRowProps {
  item: AdminTemplateItem;
  onUpdate: (itemId: string, bezeichnung: string) => void;
  onDelete: (itemId: string) => void;
}

const TemplateItemRow: React.FC<TemplateItemRowProps> = ({
  item,
  onUpdate,
  onDelete,
}) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(item.bezeichnung);

  const handleSave = () => {
    if (editValue.trim() && editValue.trim() !== item.bezeichnung) {
      onUpdate(item.id, editValue.trim());
    }
    setEditing(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.5,
        ml: "-42px",
      }}
    >
      <Box
        sx={{
          width: 40,
          display: "flex",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {!item.is_locked ? (
          <Tooltip title="Position entfernen">
            <IconButton
              size="small"
              onClick={() => onDelete(item.id)}
              sx={{
                color: "text.disabled",
                "&:hover": { color: "error.main" },
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Box sx={{ width: 40 }} />
        )}
      </Box>
      {editing && !item.is_locked ? (
        <ClickAwayListener onClickAway={handleSave}>
          <TextField
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") {
                setEditValue(item.bezeichnung);
                setEditing(false);
              }
            }}
            autoFocus
            size="small"
            variant="standard"
            fullWidth
            sx={{ "& .MuiInput-input": { fontSize: "0.875rem" } }}
          />
        </ClickAwayListener>
      ) : (
        <Typography
          variant="body2"
          onClick={() => !item.is_locked && setEditing(true)}
          sx={{
            flex: 1,
            cursor: item.is_locked ? "default" : "pointer",
            py: 0.25,
            px: item.is_locked ? 0 : 0.5,
            borderRadius: 1,
            "&:hover": item.is_locked
              ? {}
              : { backgroundColor: colors.opacity.black04 },
          }}
        >
          {item.bezeichnung}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Einzelner Abschnitt
 */
interface TemplateSectionBlockProps {
  section: AdminTemplateSection;
  onUpdateSectionLabel: (sectionId: string, label: string) => void;
  onDeleteSection: (section: AdminTemplateSection) => void;
  onAddItem: (sectionId: string) => void;
  onUpdateItem: (
    sectionId: string,
    itemId: string,
    bezeichnung: string,
  ) => void;
  onDeleteItem: (sectionId: string, itemId: string) => void;
}

const TemplateSectionBlock: React.FC<TemplateSectionBlockProps> = ({
  section,
  onUpdateSectionLabel,
  onDeleteSection,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
}) => {
  // Für gesperrte Sections: title-String übergeben (normale Anzeige)
  // Für editierbare Sections: titleNode mit EditableSectionLabel + actions mit Delete-Button
  const titleNode = !section.is_locked ? (
    <EditableLabel
      value={section.custom_label}
      trigger="pencil"
      variant="h6"
      minWidth={200}
      onSave={(label) => onUpdateSectionLabel(section.id, label)}
    />
  ) : undefined;

  const sectionActions = !section.is_locked ? (
    <Tooltip title="Abschnitt löschen">
      <IconButton
        size="small"
        onClick={() => onDeleteSection(section)}
        sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
      >
        <DeleteOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : undefined;

  return (
    <SubSection
      title={section.is_locked ? section.custom_label : ""}
      titleNode={titleNode}
      actions={sectionActions}
      defaultExpanded
    >
      {section.items.map((item) => (
        <TemplateItemRow
          key={item.id}
          item={item}
          onUpdate={(itemId, bezeichnung) =>
            onUpdateItem(section.id, itemId, bezeichnung)
          }
          onDelete={(itemId) => onDeleteItem(section.id, itemId)}
        />
      ))}
      {!section.is_locked && (
        <Box sx={{ mt: 1, ml: "-42px", display: "flex" }}>
          <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
            <Tooltip title="Position anlegen">
              <IconButton
                size="small"
                onClick={() => onAddItem(section.id)}
                sx={{
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.light",
                    color: "primary.contrastText",
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </SubSection>
  );
};

const InvestmentskostenSection: React.FC<InvestmentskostenSectionProps> = ({
  orgId,
}) => {
  const {
    sections,
    addSection,
    updateSection,
    removeSection,
    addItem,
    updateItem,
    removeItem,
  } = useAdminCapexTemplate(orgId);
  const { showError } = useToast();
  const [sectionToDelete, setSectionToDelete] =
    useState<AdminTemplateSection | null>(null);

  const handleAddSection = async () => {
    try {
      await addSection({ custom_label: "Neuer Abschnitt" });
    } catch {
      showError("Abschnitt konnte nicht angelegt werden");
    }
  };

  const handleUpdateSectionLabel = async (sectionId: string, label: string) => {
    try {
      await updateSection(sectionId, { custom_label: label });
    } catch {
      showError("Abschnitt konnte nicht umbenannt werden");
    }
  };

  const handleConfirmDeleteSection = async () => {
    if (!sectionToDelete) return;
    try {
      await removeSection(sectionToDelete.id);
    } catch {
      showError("Abschnitt konnte nicht gelöscht werden");
    } finally {
      setSectionToDelete(null);
    }
  };

  const handleAddItem = async (sectionId: string) => {
    try {
      await addItem(sectionId, { bezeichnung: "Neue Position" });
    } catch {
      showError("Position konnte nicht angelegt werden");
    }
  };

  const handleUpdateItem = async (
    sectionId: string,
    itemId: string,
    bezeichnung: string,
  ) => {
    try {
      await updateItem(sectionId, itemId, { bezeichnung });
    } catch {
      showError("Position konnte nicht umbenannt werden");
    }
  };

  const handleDeleteItem = async (sectionId: string, itemId: string) => {
    try {
      await removeItem(sectionId, itemId);
    } catch {
      showError("Position konnte nicht gelöscht werden");
    }
  };

  return (
    <>
      <Section
        title="Investitionskosten"
        actions={
          <Button
            variant="primary"
            startIcon={<AddIcon />}
            onClick={handleAddSection}
          >
            Abschnitt anlegen
          </Button>
        }
      >
        {sections.map((section) => (
          <TemplateSectionBlock
            key={section.id}
            section={section}
            onUpdateSectionLabel={handleUpdateSectionLabel}
            onDeleteSection={setSectionToDelete}
            onAddItem={handleAddItem}
            onUpdateItem={handleUpdateItem}
            onDeleteItem={handleDeleteItem}
          />
        ))}
      </Section>

      <DialogTemplate
        open={sectionToDelete !== null}
        onClose={() => setSectionToDelete(null)}
        title="Löschen bestätigen"
        maxWidth="xs"
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setSectionToDelete(null)}
            >
              Abbrechen
            </Button>
            <Button variant="primary" onClick={handleConfirmDeleteSection}>
              Löschen
            </Button>
          </>
        }
      >
        <Typography>
          Abschnitt <strong>„{sectionToDelete?.custom_label}"</strong> wirklich
          löschen? Alle enthaltenen Positionen werden ebenfalls entfernt.
        </Typography>
      </DialogTemplate>
    </>
  );
};

export default InvestmentskostenSection;
